import { apiRequest } from './client';

// --- Types ---

export type ToothSpacingScheduleItem = {
  readonly id: string;
  readonly treatment_id: string;
  readonly interval_days: number;
  readonly start_date: string;
  readonly end_date: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
};

export type ToothSpacingEntryListItem = {
  readonly id: string;
  readonly due_date: string;
  readonly status: 'pending' | 'confirmed';
  readonly confirmed_at: string | null;
  readonly cycle_number: number | null;
  readonly stage_title: string | null;
  readonly video_submitted: boolean;
  readonly description_preview: string | null;
  readonly requires_video: boolean;
};

export type ToothSpacingEntryDetail = {
  readonly id: string;
  readonly due_date: string;
  readonly status: 'pending' | 'confirmed';
  readonly confirmed_at: string | null;
  readonly requires_video: boolean;
  readonly video_submitted: boolean;
  readonly video_url: string | null;
  readonly description: string | null;
  readonly cycle_number: number | null;
  readonly stage_title: string | null;
};

export type CreateToothSpacingSchedulePayload = {
  readonly treatment_id: string;
  readonly interval_days: number;
  readonly start_date: string;
  readonly end_date?: string | null;
};

export type UpdateToothSpacingSchedulePayload = {
  readonly interval_days?: number;
  readonly end_date?: string | null;
  readonly is_active?: boolean;
};

// --- Schedules ---

export function getToothSpacingSchedules(
  patientId: string,
  treatmentId: string,
  accessToken: string,
): Promise<readonly ToothSpacingScheduleItem[]> {
  return apiRequest<readonly ToothSpacingScheduleItem[]>(
    `/admin/patients/${patientId}/tooth-spacing-schedules?treatment_id=${encodeURIComponent(treatmentId)}`,
    { method: 'GET', token: accessToken },
  );
}

export function createToothSpacingSchedule(
  patientId: string,
  payload: CreateToothSpacingSchedulePayload,
  accessToken: string,
): Promise<ToothSpacingScheduleItem> {
  return apiRequest<ToothSpacingScheduleItem>(
    `/admin/patients/${patientId}/tooth-spacing-schedules`,
    { method: 'POST', body: payload, token: accessToken },
  );
}

export function updateToothSpacingSchedule(
  patientId: string,
  scheduleId: string,
  payload: UpdateToothSpacingSchedulePayload,
  accessToken: string,
): Promise<ToothSpacingScheduleItem> {
  return apiRequest<ToothSpacingScheduleItem>(
    `/admin/patients/${patientId}/tooth-spacing-schedules/${scheduleId}`,
    { method: 'PATCH', body: payload, token: accessToken },
  );
}

export function deleteToothSpacingSchedule(
  patientId: string,
  scheduleId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/tooth-spacing-schedules/${scheduleId}`,
    { method: 'DELETE', token: accessToken },
  );
}

// --- Entries ---

export function getToothSpacingEntries(
  patientId: string,
  accessToken: string,
  params?: { status?: string; limit?: number; offset?: number },
): Promise<readonly ToothSpacingEntryListItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('entry_status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const qs = searchParams.toString();
  const path = `/admin/patients/${patientId}/tooth-spacings${qs ? `?${qs}` : ''}`;
  return apiRequest<readonly ToothSpacingEntryListItem[]>(path, {
    method: 'GET',
    token: accessToken,
  });
}

export function getToothSpacingEntryDetail(
  patientId: string,
  entryId: string,
  accessToken: string,
): Promise<ToothSpacingEntryDetail> {
  return apiRequest<ToothSpacingEntryDetail>(
    `/admin/patients/${patientId}/tooth-spacings/${entryId}`,
    { method: 'GET', token: accessToken },
  );
}

export function getToothSpacingVideoUrl(patientId: string, entryId: string): string {
  return `/admin/patients/${patientId}/tooth-spacings/${entryId}/video/content`;
}
